/**
 * useSearchReplace Hook
 * Provides search & replace functionality for a TipTap editor
 * using ProseMirror decorations for match highlighting.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const searchPluginKey = new PluginKey('searchReplace');

export interface SearchReplaceState {
  searchTerm: string;
  replaceTerm: string;
  matchCount: number;
  currentMatch: number;
  caseSensitive: boolean;
}

interface MatchRange {
  from: number;
  to: number;
}

function findMatches(doc: any, searchTerm: string, caseSensitive: boolean): MatchRange[] {
  if (!searchTerm || searchTerm.length < 2) return [];

  const matches: MatchRange[] = [];
  const text = doc.textBetween(0, doc.content.size, '\n', '\0');
  const term = caseSensitive ? searchTerm : searchTerm.toLowerCase();
  const content = caseSensitive ? text : text.toLowerCase();

  let pos = 0;
  while (pos < content.length) {
    const index = content.indexOf(term, pos);
    if (index === -1) break;

    // Map text offset to document position
    let docPos = 0;
    let textOffset = 0;
    doc.descendants((node: any, nodePos: number) => {
      if (textOffset <= index && node.isText) {
        const nodeText = node.text || '';
        const startOffset = textOffset;
        const endOffset = textOffset + nodeText.length;
        if (index >= startOffset && index < endOffset) {
          docPos = nodePos + (index - startOffset);
        }
        textOffset = endOffset;
      } else if (node.isBlock && textOffset < index) {
        textOffset += 1; // newline separator
      }
      return textOffset <= index;
    });

    if (docPos > 0) {
      matches.push({ from: docPos, to: docPos + searchTerm.length });
    }
    pos = index + 1;
  }

  return matches;
}

function createDecorations(doc: any, matches: MatchRange[], currentIndex: number): DecorationSet {
  const decorations = matches.map((match, i) =>
    Decoration.inline(match.from, match.to, {
      class: i === currentIndex ? 'search-highlight-active' : 'search-highlight',
    })
  );
  return DecorationSet.create(doc, decorations);
}

export function useSearchReplace(editor: Editor | null) {
  const [state, setState] = useState<SearchReplaceState>({
    searchTerm: '',
    replaceTerm: '',
    matchCount: 0,
    currentMatch: 0,
    caseSensitive: false,
  });

  const matchesRef = useRef<MatchRange[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateDecorations = useCallback(() => {
    if (!editor) return;

    const matches = findMatches(editor.state.doc, state.searchTerm, state.caseSensitive);
    matchesRef.current = matches;

    const currentIndex = matches.length > 0 ? Math.min(state.currentMatch, matches.length - 1) : 0;

    setState(prev => ({
      ...prev,
      matchCount: matches.length,
      currentMatch: currentIndex,
    }));

    // Remove existing plugin and add new one with updated decorations
    try { editor.unregisterPlugin(searchPluginKey); } catch { /* not registered yet */ }

    if (matches.length > 0) {
      const plugin = new Plugin({
        key: searchPluginKey,
        state: {
          init() {
            return createDecorations(editor.state.doc, matches, currentIndex);
          },
          apply(tr, oldDecorations) {
            if (tr.docChanged) {
              const newMatches = findMatches(tr.doc, state.searchTerm, state.caseSensitive);
              matchesRef.current = newMatches;
              const idx = newMatches.length > 0 ? Math.min(currentIndex, newMatches.length - 1) : 0;
              return createDecorations(tr.doc, newMatches, idx);
            }
            return oldDecorations.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(editorState) {
            return this.getState(editorState);
          },
        },
      });
      editor.registerPlugin(plugin);
    }

    // Force view update
    editor.view.dispatch(editor.state.tr);
  }, [editor, state.searchTerm, state.caseSensitive, state.currentMatch]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(updateDecorations, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [updateDecorations]);

  const setSearchTerm = useCallback((term: string) => {
    setState(prev => ({ ...prev, searchTerm: term, currentMatch: 0 }));
  }, []);

  const setReplaceTerm = useCallback((term: string) => {
    setState(prev => ({ ...prev, replaceTerm: term }));
  }, []);

  const toggleCaseSensitive = useCallback(() => {
    setState(prev => ({ ...prev, caseSensitive: !prev.caseSensitive, currentMatch: 0 }));
  }, []);

  const nextMatch = useCallback(() => {
    setState(prev => {
      if (prev.matchCount === 0) return prev;
      const next = (prev.currentMatch + 1) % prev.matchCount;
      return { ...prev, currentMatch: next };
    });
  }, []);

  const prevMatch = useCallback(() => {
    setState(prev => {
      if (prev.matchCount === 0) return prev;
      const prev_ = (prev.currentMatch - 1 + prev.matchCount) % prev.matchCount;
      return { ...prev, currentMatch: prev_ };
    });
  }, []);

  const replaceCurrent = useCallback(() => {
    if (!editor || matchesRef.current.length === 0) return;
    const match = matchesRef.current[state.currentMatch];
    if (!match) return;

    editor
      .chain()
      .focus()
      .setTextSelection({ from: match.from, to: match.to })
      .deleteSelection()
      .insertContent(state.replaceTerm)
      .run();
  }, [editor, state.currentMatch, state.replaceTerm]);

  const replaceAll = useCallback(() => {
    if (!editor || matchesRef.current.length === 0) return;

    // Replace from last to first to preserve positions
    const matches = [...matchesRef.current].reverse();
    editor.chain().focus();

    for (const match of matches) {
      editor
        .chain()
        .setTextSelection({ from: match.from, to: match.to })
        .deleteSelection()
        .insertContent(state.replaceTerm)
        .run();
    }
  }, [editor, state.replaceTerm]);

  const clearSearch = useCallback(() => {
    setState({
      searchTerm: '',
      replaceTerm: '',
      matchCount: 0,
      currentMatch: 0,
      caseSensitive: false,
    });
    matchesRef.current = [];
    if (editor) {
      try { editor.unregisterPlugin(searchPluginKey); } catch { /* ok */ }
      editor.view.dispatch(editor.state.tr);
    }
  }, [editor]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (editor) {
        try { editor.unregisterPlugin(searchPluginKey); } catch { /* ok */ }
      }
    };
  }, [editor]);

  return {
    ...state,
    setSearchTerm,
    setReplaceTerm,
    toggleCaseSensitive,
    nextMatch,
    prevMatch,
    replaceCurrent,
    replaceAll,
    clearSearch,
  };
}
